import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { LeadOtpVerification, useLeadOtp } from "@/components/LeadOtpVerification";
import UaeWhatsAppPhoneInput from "@/components/UaeWhatsAppPhoneInput";

const createCallbackForm = (phone = "") => ({
  name: "",
  company: "",
  email: "",
  messenger_type: "whatsapp",
  messenger: phone,
  direction: "callback",
  volume: "",
  message: "",
});

export const WhatsAppFloat = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const form = createCallbackForm(phone);
  const otp = useLeadOtp({ form, resetForm: () => { setPhone(""); setOpen(false); }, t });

  const submit = (event) => {
    event.preventDefault();
    otp.submit();
  };
  const close = () => {
    if (!otp.busy) {
      otp.resetOtp();
      setOpen(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-popup"
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-40 w-[calc(100vw-2rem)] max-w-sm rounded-[1.5rem] border border-white/10 bg-[#12141C]/95 p-6 shadow-2xl backdrop-blur-xl sm:right-6"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15"><MessageCircle className="h-5 w-5 text-emerald-400" /></span>
                <h3 className="text-base font-semibold text-slate-100">{t("contact.waTitle")}</h3>
              </div>
              <button onClick={close} disabled={otp.busy} data-testid="whatsapp-popup-close" className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-200 disabled:opacity-50"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{t("contact.waText")}</p>
            <form onSubmit={submit} className="mt-4">
              {otp.stage === "form" && (
                <UaeWhatsAppPhoneInput required value={phone} onChange={setPhone} placeholder={t("contact.waPhonePh")} testId="whatsapp-phone-input" className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-emerald-400/60" />
              )}
              <LeadOtpVerification otp={otp} t={t} variant="dark" directSubmitLabel={t("contact.waSubmit")} />
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button onClick={() => setOpen((value) => !value)} data-testid="whatsapp-float" initial={{ opacity: 0, scale: 0.6, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="fixed bottom-6 right-4 z-40 flex items-center gap-3 rounded-full border border-emerald-400/40 bg-emerald-500 px-5 py-4 text-sm font-semibold text-[#06210f] shadow-[0_0_40px_rgba(16,185,129,0.35)] transition-transform duration-300 hover:scale-105 active:scale-95 sm:right-6">
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">WhatsApp</span>
        <span className="absolute -right-1 -top-1 flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-300" /></span>
      </motion.button>
    </>
  );
};
